import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { filter, map, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import * as fromStore from '../../store';
import { UserSubscription } from '../../user/user-subscription';
import { User } from '../../user/user';

@Injectable({
  providedIn: 'root'
})
export class PaymentSubscriptionGuard implements CanActivate {
  constructor(
    private router: Router,
    private store: Store<fromStore.DbStoreState>
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    return combineLatest([
      this.store.select(fromStore.getCompanyInfo),
      this.store.select(fromStore.getUserAvailableHostingSubscriptions()),
      this.store.select(fromStore.getUserAvailableSubscriptions()),
      this.store.select(fromStore.getCompanyInfoLoaded),
      this.store.select(fromStore.getUserAvailableHostingSubscriptionsLoaded),
      this.store.select(fromStore.getUser),
      this.store.select(fromStore.getUserLoaded)
    ]).pipe(
      filter(([companyInfo, hostingSubscriptions, allSubscriptions, companyInfoLoaded, subscriptionsLoaded, user, userLoaded]) =>
        // userLoaded also covers planDescriptionMap being loaded, which UserSubscriptionMapper
        // needs to populate sub.plan (planPrice) - without it every subscription's plan is null
        companyInfoLoaded && subscriptionsLoaded && userLoaded
      ),
      take(1),
      map(([companyInfo, hostingSubscriptions, allSubscriptions, companyInfoLoaded, subscriptionsLoaded, user, userLoaded]) => {
        console.log('[PaymentSubscriptionGuard] Loaded states:', {
          companyInfoLoaded,
          subscriptionsLoaded,
          hostingSubscriptionsCount: hostingSubscriptions?.length || 0,
          allSubscriptionsCount: allSubscriptions?.length || 0
        });

        console.log('[PaymentSubscriptionGuard] Hosting subscriptions:',
          hostingSubscriptions?.map(sub => ({
            title: sub.title,
            planPrice: sub.plan?.planPrice,
            category: sub.category,
            type: sub.type
          }))
        );

        // Use hostingSubscriptions - this contains the actual available subscriptions
        const availableSubscriptions = hostingSubscriptions;
        const pageId = route.data['pageId'];
        const queryParams = route.queryParams;
        const hasInvitation = !!queryParams['invitation_data'];

        // Decode invitation data to get product_id and is_non_billable
        let invitationData: any = null;
        let invitationProductId: number | null = null;
        let isInvitationNonBillable = false;
        if (hasInvitation) {
          try {
            const decoded = atob(queryParams['invitation_data']);
            invitationData = JSON.parse(decoded);
            invitationProductId = invitationData?.product_id || null;
            isInvitationNonBillable = invitationData?.is_non_billable === true;
            console.log('[PaymentSubscriptionGuard] Invitation data:', {
              is_non_billable: isInvitationNonBillable,
              product_id: invitationProductId
            });
          } catch (e) {
            console.error('[PaymentSubscriptionGuard] Failed to decode invitation_data', e);
          }
        }

        // Get product_id from URL for checkout page
        const urlProductId = queryParams['product_id'] ? parseInt(queryParams['product_id'], 10) : null;

        // Determine scenario based on payment provider and subscriptions
        const hasPaymentSetup = companyInfo?.payment_provider?.connected &&
                               companyInfo?.payment_provider?.provider !== null;
        const hasNotPaidAvailable = availableSubscriptions?.some((sub: UserSubscription) => sub.plan?.planPrice === 0) || false;
        const hasPaidPlansAvailable = availableSubscriptions?.some((sub: UserSubscription) => sub.plan?.planPrice > 0) || false;

        // Debug logging - detailed subscriptions info
        const subscriptionDetails = availableSubscriptions?.map(sub => ({
            title: sub.title,
            planPrice: sub.plan?.planPrice,
            planPriceType: typeof sub.plan?.planPrice,
            trialPeriodNumber: sub.plan?.trialPeriodNumber,
            type: sub.type,
            category: sub.category,
            isFreeTrial: sub.plan?.planPrice === 0,
            isTrial: sub.plan?.trialPeriodNumber > 0
          }));

        console.log('[PaymentSubscriptionGuard] Available subscriptions DETAILED:', subscriptionDetails);
        console.log('[PaymentSubscriptionGuard] Trial detection check:',
          availableSubscriptions?.map(sub => ({
            title: sub.title,
            'planPrice === 0': sub.plan?.planPrice === 0,
            'planPrice': sub.plan?.planPrice,
            'trialPeriodNumber > 0': sub.plan?.trialPeriodNumber > 0,
            'trialPeriodNumber': sub.plan?.trialPeriodNumber
          }))
        );

        console.log('[PaymentSubscriptionGuard] Debug info:', {
          pageId,
          hasPaymentSetup,
          hasNotPaidAvailable,
          hasPaidPlansAvailable,
          hasInvitation,
          availableSubscriptions: availableSubscriptions?.length || 0,
          companyInfo: companyInfo?.payment_provider
        });

        const scenario = this.determineScenario(hasPaymentSetup, hasNotPaidAvailable, hasPaidPlansAvailable);
        console.log('[PaymentSubscriptionGuard] Determined scenario:', scenario);

        if (pageId === 'sign-up') {
          const result = this.evaluateSignUpAccess(
            scenario,
            availableSubscriptions,
            hasInvitation,
            isInvitationNonBillable,
            invitationProductId,
            user
          );
          console.log('[PaymentSubscriptionGuard] Sign-up access result:', result);
          return result;
        } else if (pageId === 'checkout') {
          const checkoutProductId = invitationProductId || urlProductId;
          const result = this.evaluateCheckoutAccess(
            scenario,
            availableSubscriptions,
            checkoutProductId,
            isInvitationNonBillable,
            user,
          );
          console.log('[PaymentSubscriptionGuard] Checkout access result:', result);
          return result;
        }

        return true;
      })
    ).toPromise();
  }

  private determineScenario(hasPaymentSetup: boolean, hasNotPaidAvailable: boolean, hasPaidPlansAvailable: boolean): number {
    // Scenarios 1-3: Payment connected (payment_provider.connected === true)
    if (hasPaymentSetup) {
      if (hasNotPaidAvailable || hasPaidPlansAvailable) return 1;  // Trial or paid plans available
      return 3; // No subscriptions at all
    }

    // Scenarios 4-5: Payment NOT connected (payment_provider.connected === false OR provider === null)
    if (!hasPaymentSetup) {
      if (hasNotPaidAvailable) return 5; // No payment + Trial available
      return 4; // No payment + No subscriptions
    }

    // Default to scenario 4 (most restrictive)
    return 4;
  }

  private evaluateSignUpAccess(
    scenario: number,
    availableSubscriptions: UserSubscription[],
    hasInvitation: boolean,
    isInvitationNonBillable: boolean,
    invitationProductId: number | null,
    user: User
  ): boolean {
    // Non-billable invitations should always allow sign-up access regardless of scenario
    if (hasInvitation && isInvitationNonBillable) {
      console.log('[PaymentSubscriptionGuard] Non-billable invitation, allowing sign-up for product_id:', invitationProductId);
      return true;
    }

    const hasTrialAvailable = availableSubscriptions?.some((sub: UserSubscription) =>
      sub.plan?.planPrice === 0 || sub.plan?.trialPeriodNumber > 0
    );

    switch (scenario) {
      case 1:
      case 5:
        // Sign-up logic:
        // 1. No invitation → Check free trial
        // 2. Invitation with is_non_billable: false (paid) → Check free trial

        // No invitation OR billable invitation → Check free trial
        if (hasTrialAvailable) {
          console.log('[PaymentSubscriptionGuard] Free trial available, allowing sign-up');
          return true;
        }

        console.warn('[PaymentSubscriptionGuard] No free trial available');
        // If user is authenticated, redirect to home instead of 404
        if (user) {
          console.log('[PaymentSubscriptionGuard] User is authenticated, redirecting to /');
          this.router.navigate(['/']);
        } else {
          this.router.navigate(['/404']);
        }
        return false;

      case 3:
      case 4:
        // If user is authenticated, redirect to home instead of 404
        if (user) {
          console.log('[PaymentSubscriptionGuard] User is authenticated, redirecting to /');
          this.router.navigate(['/']);
        } else {
          this.router.navigate(['/404']);
        }
        return false;

      default:
        // If user is authenticated, redirect to home instead of 404
        if (user) {
          console.log('[PaymentSubscriptionGuard] User is authenticated, redirecting to /');
          this.router.navigate(['/']);
        } else {
          this.router.navigate(['/404']);
        }
        return false;
    }
  }

  private evaluateCheckoutAccess(
    scenario: number,
    availableSubscriptions: UserSubscription[],
    productId: number | null,
    isInvitationNonBillable: boolean = false,
    user: User = null,
  ): boolean {
    // Non-billable invitations should always allow checkout access
    if (isInvitationNonBillable && productId) {
      console.log('[PaymentSubscriptionGuard] Non-billable invitation, allowing checkout for product_id:', productId);
      return true;
    }

    // Logged-in users should ALWAYS go to upgrade-plan first
    // Upgrade-plan will check if plans are available and redirect to / if none
    if (user) {
      console.log('[PaymentSubscriptionGuard] Logged-in user attempting checkout, redirecting to /upgrade-plan');
      this.router.navigate(['/upgrade-plan']);
      return false;
    }

    // Guest users only - check product_id and subscriptions
    switch (scenario) {
      case 1:
        // Checkout logic for GUEST users:
        // - REQUIRES product_id (from URL or invitation)
        // - If no product_id → 404
        // - If product_id exists → Must match subscriptions, else 404

        if (!productId) {
          // No product_id - BLOCK
          console.warn('[PaymentSubscriptionGuard] Guest checkout requires product_id (from URL or invitation)');
          this.router.navigate(['/404']);
          return false;
        }

        const subscription = availableSubscriptions?.find((sub: UserSubscription) =>
          sub.id === productId
        );

        if (subscription) {
          console.log('[PaymentSubscriptionGuard] Product found for guest checkout:', subscription.title);
          return true;
        }

        // Product_id provided but not found - BLOCK
        console.warn('[PaymentSubscriptionGuard] Product not found in subscriptions for guest:', productId);
        this.router.navigate(['/404']);
        return false;

      case 3:
      case 4:
      case 5:
        // Scenarios 3-5: Various failure cases
        // - Scenario 3: Payment connected + No subscriptions at all
        // - Scenario 4: Payment NOT connected + No subscriptions
        // - Scenario 5: Payment NOT connected + Trial available
        // Note: Only guest users reach here (logged-in users already redirected to upgrade-plan above)
        this.router.navigate(['/404']);
        return false;

      default:
        // Unknown scenario
        // Note: Only guest users reach here (logged-in users already redirected to upgrade-plan above)
        this.router.navigate(['/404']);
        return false;
    }
  }
}
